/**
 * Tabla de instituciones con sus acciones (habilitar, deshabilitar, cambiar nombre) y el registro de
 * una nueva. Deshabilitar pide confirmación: corta el acceso de todos sus usuarios.
 */
import { useId, useState, type FormEvent } from "react";
import { mensajeDeAccion, type Institucion } from "@/shared/api";
import { t } from "@/shared/i18n";
import { fechaHora } from "@/shared/lib/fechas";
import { Aviso, Boton, Campo, Chip, Dialogo, SoloLectores, Tabla, type Columna } from "@/shared/ui";
import { useGuardarInstitucion } from "../api/consultas";
import { normalizarCodigo, validarRegistro, type ErrorInstitucion } from "../model/instituciones";
import estilos from "./Instituciones.module.css";

const i18n = t.instituciones;

type Pendiente = { tipo: "deshabilitar"; institucion: Institucion } | { tipo: "renombrar"; institucion: Institucion };

export function TablaInstituciones({
  instituciones,
  usuariosPor,
}: {
  readonly instituciones: readonly Institucion[];
  readonly usuariosPor: ReadonlyMap<string, number>;
}) {
  const guardar = useGuardarInstitucion();
  const [pendiente, setPendiente] = useState<Pendiente | null>(null);

  const columnas: Columna<Institucion>[] = [
    {
      clave: "codigo",
      titulo: i18n.columnaCodigo,
      celda: (i) => <span className={estilos.codigo}>{i.institucion}</span>,
      orden: (i) => i.institucion,
    },
    {
      clave: "nombre",
      titulo: i18n.columnaNombre,
      rotuloMovil: i18n.columnaNombre,
      orden: (i) => i.nombre,
      celda: (i) => (
        <>
          {i.nombre}
          <span className={estilos.suave}> · {i18n.usuarios(usuariosPor.get(i.institucion) ?? 0)}</span>
        </>
      ),
    },
    {
      clave: "estado",
      titulo: i18n.columnaEstado,
      rotuloMovil: i18n.columnaEstado,
      orden: (i) => (i.habilitada ? i18n.habilitada : i18n.deshabilitada),
      celda: (i) => (
        <Chip tono={i.habilitada ? "bien" : "neutro"}>{i.habilitada ? i18n.habilitada : i18n.deshabilitada}</Chip>
      ),
    },
    {
      clave: "actualizado",
      titulo: i18n.columnaActualizado,
      rotuloMovil: i18n.columnaActualizado,
      orden: (i) => i.actualizadoEn,
      celda: (i) => (
        <span className={estilos.suave}>
          {fechaHora(i.actualizadoEn)} · {i.actualizadoPor}
        </span>
      ),
    },
    {
      clave: "acciones",
      titulo: t.ficha.acciones,
      rotuloMovil: t.ficha.acciones,
      celda: (i) => (
        <div className={estilos.acciones}>
          <Boton
            chico
            disabled={guardar.isPending}
            onClick={() => {
              if (i.habilitada) setPendiente({ tipo: "deshabilitar", institucion: i });
              else guardar.mutate({ codigo: i.institucion, nombre: i.nombre, habilitada: true });
            }}
          >
            {i.habilitada ? i18n.deshabilitar : i18n.habilitar}
          </Boton>
          <Boton
            chico
            variante="enlace"
            onClick={() => {
              setPendiente({ tipo: "renombrar", institucion: i });
            }}
          >
            {i18n.renombrar}
            <SoloLectores> {i.institucion}</SoloLectores>
          </Boton>
        </div>
      ),
    },
  ];

  const cerrar = () => {
    setPendiente(null);
    guardar.reset();
  };

  return (
    <>
      {guardar.isError && !pendiente && (
        <Aviso tono="grave" rol="alert">
          {mensajeDeAccion(guardar.error)}
        </Aviso>
      )}
      <Tabla columnas={columnas} filas={instituciones} clave={(i) => i.institucion} apilada textosOrden={t.tabla} />
      <Dialogo
        abierto={pendiente?.tipo === "deshabilitar"}
        titulo={pendiente ? i18n.confirmarDeshabilitarTitulo(pendiente.institucion.institucion) : ""}
        descripcion={i18n.confirmarDeshabilitarTexto(usuariosPor.get(pendiente?.institucion.institucion ?? "") ?? 0)}
        alCerrar={cerrar}
      >
        <div className={estilos.pie}>
          <Boton onClick={cerrar}>{t.general.cancelar}</Boton>
          <Boton
            variante="principal"
            disabled={guardar.isPending}
            onClick={() => {
              if (!pendiente) return;
              const { institucion, nombre } = pendiente.institucion;
              guardar.mutate({ codigo: institucion, nombre, habilitada: false }, { onSuccess: cerrar });
            }}
          >
            {guardar.isPending ? t.general.guardando : i18n.confirmarDeshabilitar}
          </Boton>
        </div>
        {guardar.isError && <Aviso tono="grave">{mensajeDeAccion(guardar.error)}</Aviso>}
      </Dialogo>
      {pendiente?.tipo === "renombrar" && <Renombrar institucion={pendiente.institucion} alCerrar={cerrar} />}
    </>
  );
}

function Renombrar({ institucion, alCerrar }: { readonly institucion: Institucion; readonly alCerrar: () => void }) {
  const guardar = useGuardarInstitucion();
  const [nombre, setNombre] = useState(institucion.nombre);
  const [falta, setFalta] = useState(false);
  const id = useId();
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) {
      setFalta(true);
      return;
    }
    guardar.mutate(
      { codigo: institucion.institucion, nombre: nombre.trim(), habilitada: institucion.habilitada },
      { onSuccess: alCerrar },
    );
  };
  return (
    <Dialogo abierto titulo={i18n.renombrarTitulo(institucion.institucion)} alCerrar={alCerrar}>
      <form id={id} className={estilos.formulario} onSubmit={enviar} noValidate>
        <Campo
          rotulo={i18n.nombre}
          value={nombre}
          maxLength={200}
          error={falta ? i18n.errores.nombre : null}
          onChange={(e) => {
            setNombre(e.target.value);
            setFalta(false);
          }}
        />
        {guardar.isError && <Aviso tono="grave">{mensajeDeAccion(guardar.error)}</Aviso>}
        <div className={estilos.pie}>
          <Boton onClick={alCerrar}>{t.general.cancelar}</Boton>
          <Boton type="submit" variante="principal" disabled={guardar.isPending}>
            {guardar.isPending ? t.general.guardando : i18n.guardar}
          </Boton>
        </div>
      </form>
    </Dialogo>
  );
}

/** Botón y diálogo para registrar una institución nueva (queda habilitada). */
export function RegistrarInstitucion({ existentes }: { readonly existentes: readonly Institucion[] }) {
  const guardar = useGuardarInstitucion();
  const [abierto, setAbierto] = useState(false);
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState<ErrorInstitucion | null>(null);
  const [revisado, setRevisado] = useState(false);

  const cerrar = () => {
    setAbierto(false);
    setCodigo("");
    setNombre("");
    setError(null);
    setRevisado(false);
    guardar.reset();
  };
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    setRevisado(true);
    const problema = validarRegistro(codigo, nombre, existentes);
    setError(problema);
    if (problema) return;
    guardar.mutate(
      { codigo: normalizarCodigo(codigo), nombre: nombre.trim(), habilitada: true },
      { onSuccess: cerrar },
    );
  };

  return (
    <>
      <Boton
        variante="principal"
        onClick={() => {
          setAbierto(true);
        }}
      >
        {i18n.registrar}
      </Boton>
      <Dialogo abierto={abierto} titulo={i18n.registrarTitulo} descripcion={i18n.registrarTexto} alCerrar={cerrar}>
        <form className={estilos.formulario} onSubmit={enviar} noValidate>
          <Campo
            rotulo={i18n.codigo}
            ayuda={i18n.codigoAyuda}
            value={codigo}
            maxLength={32}
            autoCapitalize="characters"
            spellCheck={false}
            className={estilos.mono}
            error={revisado && (error === "codigo" || error === "existe") ? i18n.errores[error] : null}
            onChange={(e) => {
              setCodigo(e.target.value.toUpperCase());
            }}
          />
          <Campo
            rotulo={i18n.nombre}
            value={nombre}
            maxLength={200}
            error={revisado && error === "nombre" ? i18n.errores.nombre : null}
            onChange={(e) => {
              setNombre(e.target.value);
            }}
          />
          {guardar.isError && <Aviso tono="grave">{mensajeDeAccion(guardar.error)}</Aviso>}
          <div className={estilos.pie}>
            <Boton onClick={cerrar}>{t.general.cancelar}</Boton>
            <Boton type="submit" variante="principal" disabled={guardar.isPending}>
              {guardar.isPending ? t.general.guardando : i18n.registrarYHabilitar}
            </Boton>
          </div>
        </form>
      </Dialogo>
    </>
  );
}
